import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import React from 'react';
import type { ReactNode } from 'react';

import { DIALOG_TYPE as DIALOG_TYPE_CONFIRMATION } from 'components/Dialogs/ConfirmationDialog';
import type { RootModalDrawerOptions } from 'components/Drawers/standardDrawerContract';
import type { StandardDrawerVariant } from 'components/Drawers/standardDrawerContract';
import { useAppSelector } from 'hooks/redux';

import './modalRegistrations';
import { getRootModalDrawerPresentationOptions, getRootModalPresentation } from './drawerPresentation';
import ModalRenderer from './ModalRenderer';
import RootModalDrawerRenderer from './RootModalDrawerRenderer';
import { useRootModalUrlDialogs } from './useRootModalUrlDialogs';
import { useShowDialogQueryParam } from './useShowDialogQueryParam';

function RootModal(): ReactNode {
  useShowDialogQueryParam();

  const webDrawerInput = useAppSelector((state) =>
    Boolean(featureFlagsSelectors.getFeatureFlag(state, 'webDrawerInput')),
  );
  const modals = useRootModalUrlDialogs();
  const modalArray = modals.toArray();
  const drawerEntries: RootModalDrawerOptions[] = [];
  // A drawer dispatched while a true modal is already rendered below it has to
  // join the modal plane. MUI ModalManager already treats the later-mounted
  // drawer as the top layer for focus and aria-hidden, so paint order has to
  // agree or the drawer ends up under the dialog's backdrop. This remains true
  // for every later record in the ordered traversal.
  let hasUnderlyingModal = false;

  return modalArray.map((modal, index) => {
    const presentation = getRootModalPresentation(modal);
    // Overlay drawers use the nearest underlying drawer options so child modals
    // can overlay it without each dispatcher opting in.
    const { drawerOptions, plane, underlyingDrawerOptions } = getRootModalDrawerPresentationOptions({
      drawerEntries,
      hasUnderlyingModal,
      index,
      modalArray,
      webDrawerInput,
    });
    const canRenderAsOverlayDrawer = modal.type !== DIALOG_TYPE_CONFIRMATION && presentation !== 'modal';
    const shouldRenderAsDrawer =
      presentation !== 'modal' && (drawerOptions || (underlyingDrawerOptions && canRenderAsOverlayDrawer));

    if (!shouldRenderAsDrawer) {
      // Confirmations are excluded so they stay authoritative above whatever
      // opened them instead of pushing later drawers onto the modal plane.
      if (modal.type !== DIALOG_TYPE_CONFIRMATION) {
        hasUnderlyingModal = true;
      }
      return <ModalRenderer key={`${modal.type} ${modal.id}`} modal={modal} dialogType={modal.type} />;
    }

    const drawerVariant: StandardDrawerVariant = underlyingDrawerOptions ? 'overlay' : 'temporary';
    // Overlay drawers should visually belong to the base drawer: no extra
    // scrim, and inherit the underlying width unless the launched drawer
    // requested its own width.
    const resolvedDrawerOptions: RootModalDrawerOptions = {
      ...(drawerOptions ?? {}),
      showScrim: drawerVariant === 'temporary' ? (drawerOptions?.showScrim ?? true) : false,
      width:
        drawerVariant === 'overlay'
          ? (drawerOptions?.width ?? underlyingDrawerOptions?.width ?? 'Medium')
          : (drawerOptions?.width ?? 'Medium'),
    };
    drawerEntries.push(resolvedDrawerOptions);

    return (
      <RootModalDrawerRenderer
        key={`${modal.type} ${modal.id}`}
        drawerOptions={resolvedDrawerOptions}
        modal={modal}
        plane={plane}
        variant={drawerVariant}
      />
    );
  });
}

export default React.memo(RootModal);
